import React from 'react';
import { Link } from 'react-router-dom';
import { MapPinOff, ChevronRight } from 'lucide-react';
import DataTable from 'src/shared/components/data-table/DataTable';
import type { DataTableColumns, DataTableState, RowsPerPage } from 'src/shared/components/data-table/dataTableTypes';
import { SortingDirection } from 'src/shared/components/data-table/dataTableTypes';
import { Badge } from 'src/shared/components/ui/badge';
import type { RiskInsightsSiteStatus } from 'src/app/portfolio/state/api/riskInsightsRefreshEngineSlice';
import type { AdminSite } from '../../state/api/adminSitesQuery';

export type Sort = { columnId: string; direction: 'ASC' | 'DESC' } | null;

type SiteListTableProps = {
    sites: AdminSite[];
    pagination: { totalItems: number; page: number; pageSize: number };
    onPageChange: (page: number) => void;
    sort?: Sort;
    onSortChange?: (sort: Sort) => void;
    siteSyncStatusById?: Record<string, RiskInsightsSiteStatus>;
};

const columns: DataTableColumns = [
    { columnId: 'name', title: 'Site Name', isSortable: true },
    { columnId: 'country', title: 'Country', isSortable: true },
    { columnId: 'activity', title: 'Primary Activities', isSortable: true },
    { columnId: 'geography', title: 'Geography', isSortable: true },
    { columnId: 'type', title: 'Type', isSortable: true },
    { columnId: 'syncStatus', title: 'Sync Status', width: '140px' },
];

const SyncStatusCell = ({ status }: { status: RiskInsightsSiteStatus | undefined }) => {
    if (status === 'needs-sync') {
        return (
            <Badge variant='status-medium' className='whitespace-nowrap'>
                Needs sync
            </Badge>
        );
    }
    if (status === 'uptodate') {
        return (
            <Badge variant='outline' className='whitespace-nowrap'>
                Up to date
            </Badge>
        );
    }
    return <span>—</span>;
};

const emptyState = (
    <div className='flex flex-col items-center py-16 gap-4' data-testid='site-list-empty-state'>
        <div className='flex items-center justify-center w-16 h-16 rounded-full bg-grey-20'>
            <MapPinOff className='w-8 h-8 text-primary' />
        </div>
        <div className='flex flex-col items-center gap-1 max-w-sm text-center'>
            <h3 className='text-lg font-semibold text-primary'>No sites yet</h3>
            <p className='text-sm text-grey-70'>Global Nature Risk has no sites yet, or none match the current filters.</p>
        </div>
    </div>
);

const toSort = (sorted: DataTableState['sortedColumn']): Sort => {
    if (!sorted || sorted.sortedDirection === SortingDirection.NONE) return null;
    return {
        columnId: sorted.columnId,
        direction: sorted.sortedDirection === SortingDirection.ASC ? 'ASC' : 'DESC',
    };
};

const SiteListTable = ({ sites, pagination, onPageChange, sort = null, onSortChange, siteSyncStatusById = {} }: SiteListTableProps) => {
    const handleStateChange = (newState: DataTableState) => {
        if (!onSortChange) return;
        const newSort = toSort(newState.sortedColumn);
        const sortChanged = newSort?.columnId !== sort?.columnId || newSort?.direction !== sort?.direction;
        if (sortChanged) {
            onSortChange(newSort);
        }
    };

    const sortedColumn = sort
        ? { columnId: sort.columnId, sortedDirection: sort.direction === 'ASC' ? SortingDirection.ASC : SortingDirection.DESC }
        : null;

    const data = sites.map(site => [
        <Link key={`${site.id}-name`} data-testid={`site-row-${site.id}`} to={`sites/${site.id}`} className='flex items-center gap-2'>
            <span>{site.name}</span>
            {site.status === 'DRAFT' && <Badge variant={'status-medium'}>Draft</Badge>}
            <ChevronRight className='w-4 h-4 text-grey-50 shrink-0' />
        </Link>,
        site.geography?.countryName ?? '—',
        site.activity ? `${site.activity.isicDetail.code} - ${site.activity.isicDetail.description}` : '—',
        site.geography?.environment ?? '—',
        site.geography ? (site.geography.geometryType === 'ST_Point' ? 'Coordinates' : 'Shapefile') : '—',
        <SyncStatusCell key={`${site.id}-sync-status`} status={siteSyncStatusById[site.id]} />,
    ]);

    return (
        <DataTable
            columns={columns}
            state={{ data, rowsPerPage: pagination.pageSize as RowsPerPage, currentPageNumber: pagination.page, sortedColumn }}
            totalRowsForPagination={pagination.totalItems}
            onPageChange={onPageChange}
            onStateChange={handleStateChange}
            isServerSideSort={true}
            emptyStateMessage={emptyState}
        />
    );
};

export default SiteListTable;
